import React, { ComponentType, lazy, ReactNode, Suspense } from 'react';
import { JSX } from 'react/jsx-runtime';

/**
 * Instead of using children components for route elements we can use higher order components to act as middleware.
 * This way allow us to pass props more consistently and efficiently.
 */
export function pipe( ...fns: ( ( v: ComponentType ) => ComponentType )[] ) {
	// The functions must be reversed because otherwise they will render in the wrong order e.g.:
	// pipe( withFoo, withBar, withBaz )
	// will result in withBaz rendering withBar rendering withFoo which is opposite of what we want.
	return ( x: ComponentType ) => fns.reverse().reduce( ( v, f ) => f( v ), x );
}

/**
 * Suspend a lazy import for use as a react-router route component.
 */
export function suspend(
	importer: <T>() => Promise<{ default: ComponentType<T> }>,
	fallbackLayout: ReactNode,
) {
	const Component = lazy( importer );

	return function SuspendedComponent( props: JSX.IntrinsicAttributes ) {
		return (
			<Suspense fallback={ fallbackLayout }>
				<Component { ...props } />
			</Suspense>
		);
	}
}
